import {Col, Container, Row} from "react-bootstrap";
import {useEffect, useState} from "react";
import { axiosPrivate } from "../api/axios";
import {CircularProgress, FormControl, TextField, Button} from "@mui/material";
import {Link} from "react-router-dom";
import { formatCurrency } from "../functions/formatCurrency";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import "../assets/styles/pages/_orders.scss";

dayjs.extend(utc);
dayjs.extend(timezone);

export default function Orders({summary}) {
  const [isLoading, setIsLoading] = useState(false);
  const [query, setQuery] = useState('');
  const [orders, setOrders] = useState([]);

  const handleSubmit = async (event) => {
    event && event.preventDefault();

    setIsLoading(true);
    await axiosPrivate.get('/api/exo/orders/list', {
        params: {
          q: query
        }
      })
      .then((response) => {
        setOrders(response)
      })
      .catch((error) => {
        if (error.response) {
          console.log(error.response.data); // => the response payload
        }
      })
      .finally(() => {
        setIsLoading(false);
      })
  }

  useEffect(() => {
      (async () => {
        setIsLoading(true);

        await axiosPrivate.get('/api/exo/orders/list', {
            params: {
              summary: summary,
            }
          })
          .then((response) => {
            setOrders(response)
          })
          .catch((error) => {
            if (error.response) {
              console.log(error.response.data); // => the response payload
            }
          })
          .finally(() => {
            setIsLoading(false);
          })
      })()
  },[setOrders, setIsLoading, summary]);

  const getStatusBadge = (status) => {
    const statusMap = {
      '1': { text: "Partly Processed", class: "status-partly" },
      '2': { text: "Fully Processed", class: "status-fully" }
    };
    
    return statusMap[status] || { text: "Pending", class: "status-pending" };
  };

  return (
    <Container>

      <Row className="mb-3 border-bottom pb-1 align-items-end align-content-end">
        <Col xs={12} sm={4} className="pb-2">
          {summary ? 
            <span><strong>Recent Orders</strong></span> : 
            <span><strong>Orders Index</strong> found {orders?.data?.length} records.</span>
          }
        </Col>
        <Col xs={12} sm={8} className="text-end justify-content-end align-self-end">
          {!summary ? (
            <form className="d-flex justify-content-end gap-2" onSubmit={handleSubmit}>
              <FormControl>
                <TextField 
                  style={{width: '150px'}} 
                  variant="outlined" 
                  size="small" 
                  id="order-search"
                  label="Search Orders" 
                  name="search_orders"
                  onChange={e => setQuery(e.target.value)}
                />
              </FormControl>
              <Button type="submit" variant="contained">
                Search
              </Button>
            </form>
          ) : (
            <Link to="/orders/paginated">View more orders</Link>
          )}
        </Col>
      </Row>

      {/* Desktop Table View */}
      <table className="order-index">
        <thead>
          <tr className="text-black">
            <th>Order #</th>
            <th>Date</th>
            <th>YourRef</th>
            <th>Address</th>
            <th className="text-end">Order Total</th>
            <th>Status</th>
            <th>Action</th>
          </tr>
        </thead>
        <tbody>
          {isLoading ? (
            <tr>
              <td colSpan="7" className="text-center">
                <CircularProgress/>
              </td>
            </tr>
          ) : orders?.data?.length ? (
            orders.data.map((order) => {
              const statusInfo = getStatusBadge(order.STATUS);
              return (
                <tr key={order.SEQNO}>
                  <td>{order.SEQNO}</td>
                  <td>{dayjs(order.ORDERDATE).format('D MMMM YYYY')}</td>
                  <td>{order.CUSTORDERNO}</td>
                  <td>
                    {[order.ADDRESS1, order.ADDRESS2, order.ADDRESS3, order.ADDRESS4, order.ADDRESS5]
                      .filter(Boolean)
                      .join(' ')
                    }
                  </td>
                  <td className="text-end">{formatCurrency(order.ORDTOTAL)}</td>
                  <td>
                    <span className={`status-badge ${statusInfo.class}`}>
                      {statusInfo.text}
                    </span>
                  </td>
                  <td className="text-center">
                    <Link to={"/order/" + order.SEQNO} className="action view">
                      View
                    </Link>
                  </td>
                </tr>
              );
            })
          ) : (
            <tr>
              <td colSpan="7" className="text-center">
                No order records found
              </td>
            </tr>
          )}
        </tbody>
      </table>

      {/* Mobile Card View */}
      <div className="mobile-cards">
        {isLoading ? (
          <div className="loading-card">
            <CircularProgress/>
            <p>Loading orders...</p>
          </div>
        ) : orders?.data?.length ? (
          orders.data.map((order) => {
            const statusInfo = getStatusBadge(order.STATUS);
            const fullAddress = [order.ADDRESS1, order.ADDRESS2, order.ADDRESS3, order.ADDRESS4, order.ADDRESS5]
              .filter(Boolean)
              .join(' ');

            return (
              <div key={order.SEQNO} className="order-card">
                <div className="card-header">
                  <span className="order-number">Order #{order.SEQNO}</span>
                  <span className={`status-badge ${statusInfo.class}`}>
                    {statusInfo.text}
                  </span>
                </div>
                
                <div className="card-body">
                  <div className="card-field">
                    <span className="field-label">Date:</span>
                    <span className="field-value">
                      {dayjs(order.ORDERDATE).format('D MMMM YYYY')}
                    </span>
                  </div>
                  
                  <div className="card-field">
                    <span className="field-label">Your Ref:</span>
                    <span className="field-value">{order.CUSTORDERNO || 'N/A'}</span>
                  </div>
                  
                  <div className="card-field">
                    <span className="field-label">Address:</span>
                    <span className="field-value">{fullAddress || 'N/A'}</span>
                  </div>
                  
                  <div className="card-field">
                    <span className="field-label">Total:</span>
                    <span className="field-value total-amount">
                      {formatCurrency(order.ORDTOTAL)}
                    </span>
                  </div>
                </div>
                
                <div className="card-actions">
                  <Link to={"/order/" + order.SEQNO} className="action view">
                    View Order Details
                  </Link>
                </div>
              </div>
            );
          })
        ) : (
          <div className="no-orders-card">
            <p>No order records found</p>
          </div>
        )}
      </div>
    </Container>
  );
}